import { SEARCH_CONTEXT } from '@/features/searchV2/constants/tracking';
import { getCookie, isAndroidPhone, isIPhone, isServer, setCookie } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { track } from '@/helpers/tracking';
import { _s } from '@/locale';
import type { Catalogue } from '@bokadirekt/tracking-schema/marketplace';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from '../forms/buttons';
import styles from './AppPromotionCard.module.scss';
import Card from './Card';

const baseTranslationKey = 'components.elements.Card.DealsPromotionCard';
const LOCAL_STORAGE_BASE_KEY = 'dealsPromoDismissed';
const whiteListedPages: Catalogue['Banner Clicked']['screen_name'][] = [
  'home',
  'my_bookings_upcoming',
  'my_bookings_finished',
  'favourites',
];
// The schema spells the favourites page `favourites`; web's v1 screen name stays `favorites`.
const getSchemaScreenName = (pathname: string) => {
  const screenName = getScreenNameFromPath(pathname);
  return screenName === 'favorites' ? 'favourites' : screenName;
};
const isWhiteListedPage = (screenName: string): screenName is (typeof whiteListedPages)[number] =>
  whiteListedPages.some((page) => page === screenName);

export const allowDealsPromotionCard = (dealsPromoFlag: boolean, locationPathname: string | undefined) => {
  if (isServer) return false;

  if (!(isIPhone() || isAndroidPhone())) {
    return false;
  }

  if (!dealsPromoFlag) {
    return false;
  }

  const screenName = getSchemaScreenName(locationPathname);
  if (!isWhiteListedPage(screenName) || (screenName === 'home' && locationPathname !== '/')) {
    return false;
  }

  /* do not show if already shown in current session */
  if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
    return false;
  }

  /* Deals Promotion banner is capped at 1 impression if clicked, 2 impressions if closed */
  const cardImpressions = parseInt(getCookie(LOCAL_STORAGE_BASE_KEY)) || 0;
  return cardImpressions < 2;
};

export const checkIfDismissedInSession = () => {
  if (isServer) return true;
  if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
    return true;
  }

  return false;
};

const DealsPromotionCard = () => {
  const location = useLocation();
  const history = useHistory();

  const [hasDismissed, setHasDismissed] = useState(false);

  const [timerTriggered, setTimerTriggered] = useState(false);
  const showCard = !isServer && !hasDismissed && timerTriggered;
  const screenName = !isServer ? getSchemaScreenName(location.pathname) : undefined;

  useEffect(() => {
    if (showCard && isWhiteListedPage(screenName)) {
      track('Banner Viewed', { banner_type: 'deals_promotion_card', screen_name: screenName });
    }
  }, [showCard]);

  useEffect(() => {
    const setNewTimeout = () => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        setTimerTriggered(true);
      }, 2000);
    };

    let timer = setTimeout(() => {
      setTimerTriggered(true);
    }, 2000);

    setNewTimeout();

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleDismiss = () => {
    if (isWhiteListedPage(screenName))
      track('Banner Dismissed', { banner_type: 'deals_promotion_card', screen_name: screenName });
    setHasDismissed(true);
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    const alreadyShown = parseInt(getCookie(LOCAL_STORAGE_BASE_KEY)) || 0;
    setCookie(LOCAL_STORAGE_BASE_KEY, `${alreadyShown + 1}`, {
      path: '/',
      expires: new Date(Date.now() + 30 * 86400 * 1000),
    });
  };

  const handleDealsClick = () => {
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    setCookie(LOCAL_STORAGE_BASE_KEY, '2', { path: '/', expires: new Date(Date.now() + 30 * 86400 * 1000) });
    if (isWhiteListedPage(screenName))
      track('Banner Clicked', { banner_type: 'deals_promotion_card', screen_name: screenName });
    history.push({ pathname: '/deals-hem', state: { searchContext: SEARCH_CONTEXT.DEALS_PROMOTION_CARD } });
  };

  return showCard ? (
    <>
      <div className={`z-10003 fixed h-screen w-screen bg-black opacity-50`}></div>
      <Card
        closable={false}
        cardBackground={`bg-gradient-deals`}
        className={`z-10004 fixed bottom-[62px] p-[8px] ${styles.slideInBottom} !drop-shadow-lg`}
        onClickOutside={handleDismiss}
        navArrow={
          <img
            src="/images/card-arrow-down.svg"
            alt="arrow down icon"
            className="absolute bottom-[-3px] left-[62.5%] -translate-x-1/2"
          />
        }>
        <div className="gap-2xl py-md flex flex-col">
          <div className="gap-sm flex flex-col items-center justify-center text-center">
            <img
              src="/images/deal-basket-big.png"
              alt="deal basket"
              style={{ maxWidth: '329px', maxHeight: '192px' }}
            />
            <h2 className="text-2xl font-bold">{_s(`${baseTranslationKey}.titlePart1`)}</h2>
            <h2 className="text-2xl font-bold">
              <span className="text-highlight">{_s(`${baseTranslationKey}.titlePart2`)}</span>
              &nbsp;{_s(`${baseTranslationKey}.titlePart3`)}
            </h2>
            <p className="text-black-600 mt-sm text-sm">{_s(`${baseTranslationKey}.content`)}</p>
          </div>
          <div className="gap-sm flex flex-col">
            <Button
              variant="primary"
              onClick={handleDealsClick}
              block
              label={_s(`${baseTranslationKey}.cta`)}
              size="lg"
            />
          </div>
        </div>
      </Card>
    </>
  ) : null;
};

export default DealsPromotionCard;
